import React, { useEffect, useState } from 'react';

import { Header, Navbar } from '@C';
import { OnDuty } from '@C/CashiersOnDuty/RouteComponents/AssignDuty';
import { CreditCustomerDashboard } from '@C/CreditCustomerProfile/DashboardProfile';
import {
  CashierActiveDuty,
  DashboardCharts,
  DashboardFuelPrice,
  MiniDsr,
} from '@C/Dashboard/Components';
import { SummaryStatus } from '@C/DipReading/RouteComponents';
import ErrorBoundary from '@C/Error';
import { authService } from '@S';
import dayjs from 'dayjs';
import { useGlobalStore } from '@stores/globalStore';
import { toast } from 'react-toastify';
import axios from 'axios';
import { authHeader } from '@H';
import { confirmAlert } from 'react-confirm-alert';

import './dashboard.scoped.scss';

const Dashboard = () => {
  const role = authService.currentUserRole;
  const userId = authService.currentUserId;
  const subscriptionDetails = authService.bunkSubscriptionDetails;
  const [bannerData, setBannerData] = useState(
    useGlobalStore((state) => state.bannerData),
  );
  const claimedOffers = useGlobalStore((state) => state.getClaimedOffers);
  const expiryDate = subscriptionDetails?.subscription_expiry_date
    ? dayjs(subscriptionDetails.subscription_expiry_date)
    : null;
  const isExpiryValid = expiryDate?.isValid();
  const daysRemaining = isExpiryValid
    ? expiryDate.endOf('day').diff(dayjs().startOf('day'), 'day')
    : null;
  const rawDuration =
    subscriptionDetails?.validity ??
    subscriptionDetails?.duration ??
    subscriptionDetails?.plan_validity;
  const durationLabel = rawDuration
    ? `${rawDuration}${String(rawDuration).match(/^\d+$/) ? ' days' : ''}`
    : '--';
  const showSubscriptionBanner =
    (role === 'Dealer' || role === 'Manager') &&
    subscriptionDetails &&
    isExpiryValid &&
    daysRemaining >= 0 &&
    daysRemaining <= 5;
  const [isSubscriptionAlertDismissed, setIsSubscriptionAlertDismissed] =
    useState(false);

  const confirmApiCall = (buttonElement, button, banner, e) => {
    confirmAlert({
      title: e.title || '',
      message: e.text || '',
      buttons: [
        {
          label: e.reject_label || 'No',
          onClick: () => false,
        },
        {
          label: e.accept_label || 'Yes',
          onClick: () => dynamicApiCall(buttonElement, button, banner),
        },
      ],
    });
  };

  const dynamicApiCall = (buttonElement, button, banner) => {
    const url = `${process.env.API_URL}${button.url}`;
    const method = button.method || 'GET';
    const postAction = button.post_action || {};
    buttonElement.disabled = true; // Disable button to prevent multiple clicks
    buttonElement.classList.add('loading'); // Add loading class for visual feedback
    const headers = authHeader(1);
    axios({
      method: method,
      url: url,
      headers: headers,
    })
      .then((response) => response.data)
      .then((data) => {
        toast.success(data.message);
        buttonElement.classList.remove('loading'); // Remove loading class
        buttonElement.innerHTML = postAction.text || 'Offer claimed';
        buttonElement.disabled = postAction.disable || false;
        // add this banner to claimedOffers in global store
        useGlobalStore
          .getState()
          .setClaimedOffers([...claimedOffers(), banner]);
        removeBanner(banner);
      })
      .catch((error) => {
        console.error('API Error:', error);
      });
  };

  const removeBanner = (banner) => {
    // remove the banner from bannerData state by comparing banner.content
    const updatedBanners = bannerData.filter(
      (b) => b.content !== banner.content,
    );
    setBannerData(updatedBanners);
  };

  useEffect(() => {
    // in bannerdata, I have some buttons with data-id attributes with some actions. I need to add event listeners to those buttons after the banners are rendered.
    bannerData.forEach((banner) => {
      // check if this banner exist in claimedOffers by comparing banner.content with each banner.content in claimedOffers
      const isClaimed = claimedOffers().some(
        (claimed) => claimed.content === banner.content,
      );
      if (isClaimed) {
        // if claimed, find all buttons in this banner and disable them and change text to "Offer already claimed"
        if (banner.buttons && banner.buttons.length > 0) {
          banner.buttons.forEach((button) => {
            const buttonElement = document.querySelector(
              `button[data-id="${button.id}"]`,
            );
            if (buttonElement && button.action !== 'remove_banner') {
              buttonElement.disabled = button.post_action?.disable || true;
              buttonElement.innerHTML =
                button.post_action?.text || 'Offer already claimed';
            }
          });
        }
      }

      if (banner.buttons && banner.buttons.length > 0) {
        banner.buttons.forEach((button) => {
          const buttonElement = document.querySelector(
            `button[data-id="${button.id}"]`,
          );
          if (buttonElement) {
            buttonElement.addEventListener('click', () => {
              // Perform action based on button.action
              if (button.action === 'api') {
                if (button.confirmation_modal) {
                  confirmApiCall(
                    buttonElement,
                    button,
                    banner,
                    button.confirmation_modal,
                  );
                } else {
                  dynamicApiCall(buttonElement, button, banner);
                }
              } else if (button.action === 'remove_banner') {
                removeBanner(banner);
              }
            });
          }
        });
      }
    });
  }, [bannerData]);

  return (
    <main>
      <div className="page-wrapper">
        <Header title="Dashboard" />
        <Navbar />
        <div className="page-content">
          <section className="page-components">
            {showSubscriptionBanner && !isSubscriptionAlertDismissed && (
              <div className="notification-alert notification-alert__error subscription-expiry-top-alert">
                <p>
                  Your plan will expire on{' '}
                  <b>{expiryDate.format('DD/MM/YYYY')}</b>. Package:{' '}
                  <span>{subscriptionDetails?.package_name || '--'}</span> |
                  Plan: <span>{subscriptionDetails?.plan_name || '--'}</span> |
                  Expiry: <span>{expiryDate.format('DD/MM/YYYY')}</span>
                </p>
                <button
                  type="button"
                  className="subscription-expiry-close"
                  onClick={() => setIsSubscriptionAlertDismissed(true)}
                  aria-label="Dismiss expiry alert"
                >
                  ×
                </button>
              </div>
            )}
            <div className="page-title">
              <h1>Dashboard</h1>
            </div>
            <ErrorBoundary>
              {(role === 'Dealer' || role === 'Manager') && (
                <>
                  {bannerData.length > 0 && (
                    <div className="dashboard-banners">
                      {bannerData.map((banner, id) => (
                        <div
                          key={id}
                          className="dashboard-banner"
                          dangerouslySetInnerHTML={{ __html: banner.content }}
                        />
                      ))}
                    </div>
                  )}
                  <DashboardFuelPrice />
                </>
              )}
              {(role === 'Dealer' || role === 'Manager') && (
                <SummaryStatus className="asd" />
              )}
              {(role === 'Dealer' || role === 'Manager') && (
                <OnDuty uuid={userId} />
              )}
              {role === 'Cashier' && <CashierActiveDuty uuid={userId} />}
              {(role === 'Dealer' || role === 'Manager') && <MiniDsr />}
              {(role === 'Dealer' || role === 'Manager') && <DashboardCharts />}
              {role === 'Credit Customer' && <CreditCustomerDashboard />}
            </ErrorBoundary>
          </section>
        </div>
      </div>
    </main>
  );
};

export { Dashboard };
